export interface KeyFn<A> {
  (value: A): any;
}

export interface KeyedEntry<K, V> {
  key: K;
  value: V;
}

export function group<T, K, V>(
  values: Iterable<T>,
  fn: (value: T) => [K, V],
): Map<K, V[]> {
  const results = new Map<K, V[]>();
  for (const item of values) {
    const [key, value] = fn(item);
    let array = results.get(key);
    if (!array) {
      array = [];
      results.set(key, array);
    }
    array.push(value);
  }
  return results;
}

export function getOrSet<K, V>(map: Map<K, V>, key: K, create: () => V): V {
  if (map.has(key)) {
    return map.get(key)!;
  }
  const value = create();
  map.set(key, value);
  return value;
}

export class KeyedMap<K, V> implements Map<K, V> {
  constructor(
    private readonly keyFn: KeyFn<K>,
    entries?: [K, V][],
  ) {
    this.underlying = new Map(
      entries && entries.map(([key, value]) => [keyFn(key), { key, value }]),
    );
  }

  private readonly underlying: Map<any, KeyedEntry<K, V>>;

  clear() {
    this.underlying.clear();
  }

  delete(key: K) {
    return this.underlying.delete(this.keyFn(key));
  }

  *entries() {
    for (const { key, value } of this.underlying.values()) {
      yield <[K, V]>[key, value];
    }
  }

  forEach(
    callbackfn: (value: V, key: K, map: Map<K, V>) => void,
    thisArg?: any,
  ) {
    this.underlying.forEach(({ key, value }) => {
      callbackfn.call(thisArg, value, key, this);
    });
  }

  get(key: K) {
    const entry = this.underlying.get(this.keyFn(key));
    return entry && entry.value;
  }

  has(key: K) {
    return this.underlying.has(this.keyFn(key));
  }

  *keys() {
    for (const { key } of this.underlying.values()) {
      yield key;
    }
  }

  set(key: K, value: V) {
    this.underlying.set(this.keyFn(key), { key, value });
    return this;
  }

  get size() {
    return this.underlying.size;
  }

  *values() {
    for (const { value } of this.underlying.values()) {
      yield value;
    }
  }

  [Symbol.iterator]() {
    return this.entries();
  }

  get [Symbol.toStringTag]() {
    return this.underlying[Symbol.toStringTag];
  }
}

export class KeyedSet<T> implements Set<T> {
  constructor(
    private readonly keyFn: KeyFn<T>,
    entries?: T[],
  ) {
    this.underlying = new Map(
      entries && entries.map(value => [keyFn(value), value]),
    );
  }

  private readonly underlying: Map<any, T>;

  clear() {
    this.underlying.clear();
  }

  delete(value: T) {
    return this.underlying.delete(this.keyFn(value));
  }

  *entries() {
    for (const value of this.underlying.values()) {
      yield <[T, T]>[value, value];
    }
  }

  forEach(
    callbackfn: (value: T, value2: T, map: Set<T>) => void,
    thisArg?: any,
  ) {
    this.underlying.forEach(value => {
      callbackfn.call(thisArg, value, value, this);
    });
  }

  has(value: T) {
    return this.underlying.has(this.keyFn(value));
  }

  keys() {
    return this.underlying.values();
  }

  add(value: T) {
    this.underlying.set(this.keyFn(value), value);
    return this;
  }

  get size() {
    return this.underlying.size;
  }

  values() {
    return this.underlying.values();
  }

  [Symbol.iterator]() {
    return this.values();
  }

  get [Symbol.toStringTag]() {
    return this.underlying[Symbol.toStringTag];
  }
}

export class Counter<T> {
  private readonly _counts = new Map<T, number>();

  add(item: T) {
    this._counts.set(item, this.getCount(item) + 1);
  }

  getCount(item: T) {
    return this._counts.get(item) || 0;
  }
}
